import type {
  ComponentsObject,
  KeyByComponent,
  KeyByInstance,
  Merge,
  Mutable,
} from '../utils';
import type { RunnerItem } from '../utils/Runner';
import { Runner } from '../utils/Runner';
import {
  odieDestroyComponent,
  type Component,
  type ComponentClass,
} from './Component';
import type { Query } from './queries';
import { ID32 } from './queries';
import type { Scene } from './Scene';

/** unique id generator, because like snowflakes our entities are individual and special */
export const UIDs = {
  _ENTITY: 0,
  _COMPONENT: 0,
  _ENTITY_CLASS: 0,
};

export class Entity<
  COMPS extends ComponentClass[] = [],
  PART_COMPS extends ComponentClass[] = [],
  COMPS_OBJ extends Record<string, any> = {},
> {
  /** whether or not the class should be pooled */
  public static POOL = true;
  public static readonly ID: number;

  /** the object that holds a reference to the components on the entity */
  public readonly c: Merge<
    COMPS_OBJ & KeyByInstance<COMPS> & Partial<KeyByInstance<PART_COMPS>>
  > = {} as Merge<
    COMPS_OBJ & KeyByInstance<COMPS> & Partial<KeyByInstance<PART_COMPS>>
  >;

  /**
   * a unique id for this entity, this NEVER changes even when being pooled
   * @internal
   */
  public readonly UID = UIDs._ENTITY++;

  /**
   * a key that is specific to this entities components, used for generating queries
   * @internal
   */
  public readonly componentBitHash: ID32 = new ID32();

  // TODO: remove the need for entities to have access to its queries
  /** A reference to the queries this entity is a part of, which is automatically populated */
  public readonly queries: Query[] = [];

  /** A name to give this entity! Entities can be searched for by name within the scene */
  public name = `item${this.UID}`;

  /** A reference to the scene this entity exists in, which is automatically set by the scene when the entity is added */
  public scene!: Scene | undefined;

  /** runners for initialising and resetting components */
  public readonly runners = {
    /** A runner called when the entity is gathered from a pool. Calls `init()` on any registered objects. */
    init: new Runner('init', 1),
    /** A runner called when the entity is returned to a pool. Calls `reset()` on any registered objects. */
    reset: new Runner('reset'),
  };

  /**
   * if an object is marked for removal by the gc this is true
   * @internal
   */
  public _gc = false;
  /**
   * if an object is marked for release by the pool.
   * @internal
   */
  public _released = false;
  /**
   * if an object was created from a pool.
   * @internal
   */
  public _pooled = false;

  protected _initialised = false;

  /** components to be added on creation */
  protected readonly _staticComponents!: Set<ComponentClass>;

  constructor() {
    this._staticComponents = this._staticComponents || new Set();
    this._addStaticComponents();
  }

  /**
   * called when the entity is taken from the pool
   * @param data - the init data for this entity
   */
  public init<T extends Entity>(data?: KeyByComponent<T['c']>): void {
    if (this._initialised) return;

    this._addStaticComponents();

    const components = this.runners.init.items as unknown as Component[];

    for (let i = 0; i < components.length; i++) {
      const component = components[i];

      component.init?.(data?.[component.name as keyof typeof data] || {});
    }

    this._initialised = true;
  }

  /** called when the entity is added back to the pool */
  public reset(): void {
    this._gc = false;

    this.runners.reset.run();

    for (const key in this.c) {
      const component = this.c[key] as Component;

      if (component.signals) {
        const signals = component.signals;

        for (const i in signals) {
          signals[i].disconnectAll();
        }
      }

      if (component.dynamic)
        this._removeComponent(component.name!, component.id!);
    }

    this._initialised = false;
  }

  /**
   * create and add a component. Init data is only passed if the entity is already part of a scene
   * @param Class - the component class to create and add
   * @param data - the data to pass to the component constructor if part of a scene
   */
  public addComponent<C extends Component>(
    Class: ComponentClass<any, C>,
    data?: C['init'] extends (...args: any) => any
      ? Parameters<C['init']>[0]
      : undefined,
    staticComp = false,
  ): C {
    if (!Class.NAME) {
      throw new Error(
        '[Entity] cannot add component that does not have a name',
      );
    }

    if (this.c[Class.NAME as keyof Entity['c']]) {
      return this.c[Class.NAME as keyof Entity['c']] as C;
    }

    if (Class.ID === undefined) {
      Class.ID = UIDs._COMPONENT++;
    }

    const component = new Class() as Mutable<C>;

    component.name = Class.NAME;
    component.id = Class.ID;
    component.entity = this;

    (this.c as Record<string, Component>)[Class.NAME] = component;

    for (const i in this.runners) {
      this.runners[i as keyof Entity['runners']].add(
        component as RunnerItem<keyof Entity['runners']>,
      );
    }

    this.componentBitHash.setIndex(Class.ID);

    if (this._initialised) component.init?.(data || {});

    if (this.scene) {
      this.scene.queryManager.onEntityComponentAdded(this);
    }

    if (staticComp && !this._staticComponents.has(Class))
      this._staticComponents.add(Class);
    if (!this._staticComponents.has(Class)) component.dynamic = true;

    return component;
  }

  /**
   * removes a component of specified type
   * @param Class - the type of component to remove
   * @param name - optional name used to register component
   */
  public removeComponent(Class: ComponentClass): this {
    this._removeComponent(Class.NAME, Class.ID!);

    return this;
  }

  /** removes all components from the entity */
  public removeAllComponents(): this {
    for (const key in this.c) {
      const comp = this.c[key] as Component;

      this._removeComponent(comp.name!, comp.id!);
    }

    return this;
  }

  /**
   * gets the instance of a component on the entity
   * @param Class - component class to get instance of
   * @returns the instance of the component
   */
  public getComponent<C extends Component>(Class: ComponentClass<any, C>): C {
    return this.c[Class.NAME as keyof Entity['c']] as C;
  }

  /**
   * Sets a component to be modified and lets the query manager dispatch the "modified" event
   * @param Class - component to be set as modified
   */
  public setModified(Class: ComponentClass, data?: any): void {
    for (let i = 0; i < this.queries.length; i++) {
      this.queries[i].modifiedQuery(this, Class, data);
    }
  }

  /** adds static components to the entity */
  protected _addStaticComponents(): this {
    for (const comp of this._staticComponents) {
      this.addComponent(comp, undefined, true);
    }

    return this;
  }

  /**
   * Internally removes a component from the entity
   * @param name - name of component to remove
   * @param id - id of component to remove
   */
  protected _removeComponent(name: string, id: number): void {
    const component = this.c[name as keyof Entity['c']] as Component;

    if (!component) {
      return;
    }

    for (const i in this.runners) {
      this.runners[i as keyof Entity['runners']].remove(
        component as RunnerItem<keyof Entity['runners']>,
      );
    }

    this.componentBitHash.unsetIndex(id);

    if (this.scene) {
      this.scene.queryManager.onEntityComponentRemoved(this);
    }

    component.reset?.();

    delete this.c[name as keyof Entity['c']];

    return;
  }

  public destroy() {
    this.runners.init.destroy();
    this.runners.reset.destroy();
    for (const key in this.c) {
      const component = this.c[key] as Component;
      component.destroy?.();
      odieDestroyComponent(component);
    }
    this.removeAllComponents();
    this.scene = undefined;
  }
}

export interface EntityClass<
  COMPS extends ComponentClass[] = any,
  PART_COMPS extends ComponentClass[] = any,
  COMPS_OBJ extends ComponentsObject = {},
  E extends Entity = Entity<COMPS, PART_COMPS, COMPS_OBJ>,
> {
  POOL: boolean;
  ID: number;
  new (): E;
}
